import React from "react";

/**
 * Pronto Logo — iter 24 · brand palette autentico
 *
 * Utilizza la "P-casa" inviata dall'utente come asset PNG (mark-only.jpeg)
 * e il wordmark "PRONTO" in Montserrat Medium, color caramello (#7C8B6E).
 *
 * Variants:
 *   • full     → mark + wordmark orizzontale (header)
 *   • mark     → solo simbolo
 *   • wordmark → solo testo
 *   • stacked  → mark sopra, wordmark sotto + tagline (hero / OG / brochure)
 *
 * Tones:
 *   • dark   → caramello su panna o bianco (default)
 *   • light  → bianco/panna su sfondo caramello
 */
const CARAMEL = "#7C8B6E";
const CREAM = "#F1F4ED";

export default function ProntoLogo({
  size = 30,
  variant = "full",
  tone = "dark",
  withTagline = false,
  className = "",
  ...rest
}) {
  // The mark uses the PNG only when on a transparent / cream background;
  // when on a caramel background we need the inverse (cream-on-caramel).
  // We have both as static assets, so we just pick the right URL.
  // Override per il tile (squircle compatto solo icona, no wordmark) — usato nelle sidebar/drawer
  // dove c'è poco spazio. Il logo COMPLETO con tagline va usato direttamente con <img> nel landing.
  const useTile = rest["data-tile"] === true || rest["data-tile"] === "true";
  if (useTile) {
    // iOS-style squircle: 22.5% radius matches Apple icon grid.
    // mark-tile.png ha già un tile sage con angoli ~17.5% — il clip CSS lo porta a ~22.5%.
    return (
      <span className={className} {...rest}>
        <img
          src="/logo/mark-tile.png"
          alt="Pronto"
          width={size}
          height={size}
          style={{ width: size, height: size, objectFit: "contain", borderRadius: `${Math.round(size * 0.225)}px`, overflow: "hidden" }}
          draggable="false"
          className="select-none"
        />
      </span>
    );
  }
  const markSrc = tone === "light" ? "/logo/mark-symbol-white.png" : "/logo/mark-symbol.png";
  const wordColor = tone === "light" ? CREAM : CARAMEL;

  const Mark = (
    <img
      src={markSrc}
      alt="Pronto"
      width={size}
      height={size}
      className="flex-shrink-0 select-none"
      style={{ width: size, height: size, objectFit: "contain" }}
      draggable="false"
    />
  );

  const Wordmark = (
    <span
      className="font-brand leading-none"
      style={{ color: wordColor, fontSize: `${Math.round(size * 0.78)}px` }}
    >
      Pronto
    </span>
  );

  if (variant === "mark") return <span className={className} {...rest}>{Mark}</span>;
  if (variant === "wordmark") return <span className={className} {...rest}>{Wordmark}</span>;

  if (variant === "stacked") {
    return (
      <span
        className={`inline-flex flex-col items-center gap-3 ${className}`}
        {...rest}
      >
        {Mark}
        <span
          className="font-brand leading-none"
          style={{ color: wordColor, fontSize: `${Math.round(size * 0.55)}px` }}
        >
          Pronto
        </span>
        {withTagline && (
          <span
            className="font-brand leading-none"
            style={{
              color: wordColor,
              fontSize: `${Math.round(size * 0.16)}px`,
              opacity: 0.85,
              letterSpacing: "0.18em",
            }}
          >
            Per agenti immobiliari
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} {...rest}>
      {Mark}
      {Wordmark}
    </span>
  );
}
